/**
 * MappingEditor — admin tool to correct menu → Square item-name matches.
 * Shows each cart item's auto-matched Square name with a searchable override.
 * Overrides persist to Firestore (squareItemMap/main) and apply to all orders.
 */
import React, { useState } from 'react';
import { useOrdering } from './OrderingContext';

export default function MappingEditor() {
  const { isStaff, cart, nameMap, mappingOpen, setMappingOpen } = useOrdering();
  const [filter, setFilter] = useState({});

  if (!isStaff || !mappingOpen) return null;

  // Distinct menu names currently in the cart (the ones that will be ordered).
  const names = [...new Set(cart.items.map(i => i.name))];

  return (
    <div className="ord-overlay" onClick={() => setMappingOpen(false)}>
      <div className="ord-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label="Square item mapping">
        <div className="ord-drawer__head">
          <h3>Square Item Mapping</h3>
          <button className="ord-x" onClick={() => setMappingOpen(false)} aria-label="Close">✕</button>
        </div>
        <div className="ord-modal__body">
          <p className="ord-note">
            Match each menu item to its Square catalog name so orders show correctly in Square.
            {nameMap.loading ? ' Loading Square catalog…' : ` ${nameMap.squareNames.length} Square items found.`}
          </p>
          {names.length === 0 && <p className="ord-empty-text">Add items to your cart to map them.</p>}
          {names.map(menuName => {
            const r = nameMap.resolve(menuName);
            const q = (filter[menuName] || '').toLowerCase();
            const options = q
              ? nameMap.squareNames.filter(n => n.toLowerCase().includes(q)).slice(0, 30)
              : nameMap.squareNames.slice(0, 30);
            return (
              <div key={menuName} className="ord-map-row">
                <div className="ord-map-row__head">
                  <span className="ord-map-row__menu">{menuName}</span>
                  <span className={`ord-map-badge ord-map-badge--${r.source}`}>
                    {r.source === 'override' ? '📌 pinned' : r.source === 'auto' ? `auto ${Math.round(r.score * 100)}%` : '⚠ none'}
                  </span>
                </div>
                <div className="ord-map-row__current">→ {r.squareName}</div>
                <input
                  className="ord-map-search"
                  placeholder="Search Square items to pin…"
                  value={filter[menuName] || ''}
                  onChange={e => setFilter(f => ({ ...f, [menuName]: e.target.value }))}
                />
                <div className="ord-map-options">
                  {options.map(n => (
                    <button
                      key={n}
                      className={`ord-map-opt${r.squareName === n ? ' ord-map-opt--on' : ''}`}
                      onClick={() => nameMap.setOverride(menuName, n)}>
                      {n}
                    </button>
                  ))}
                  {nameMap.overrides[menuName] && (
                    <button className="ord-map-opt ord-map-opt--clear" onClick={() => nameMap.setOverride(menuName, null)}>
                      ✕ Clear pin (use auto-match)
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
